// src/config/fnct.config.ts — SCE Issues
// Réglages des FNC-T (chantier FNC-T, lot F2) : valeurs de la base, libellés
// français, N° de test, responsables et délais. FIGÉS ici (décision de
// Guillaume du 06/10) : pas recalculés depuis l'historique.
//
// Les VALEURS restent celles de la base Excel, en anglais, à l'identique —
// fautes comprises (« Electical ») : statistiques et rapport client les
// comptent telles quelles. L'interface montre les libellés français.

import type { CategorieAction } from '@/types/fnct.types'

/**
 * Essai tant que la base Excel fait foi (jusqu'au lot F4) : la FNC-T est
 * créée dans SCE_FNCT, mais RIEN n'est écrit sur le problème (ni badge, ni
 * référence, ni activité) — les autres utilisateurs ne voient pas d'essai.
 * À passer à `false` le jour de la bascule, après l'import complet.
 */
export const FNCT_EN_ESSAI = true

/**
 * Masque tout ce qui touche aux FNC-T dans le profil de l'administrateur
 * (entrée du menu du compte, ligne « Ouvrir une FNC-T » des fiches), le temps
 * d'une présentation à l'écran. Repasser à `false` pour les retrouver.
 */
export const FNCT_MASQUEE = false

export interface CategorieFnct {
  valeur: string
  libelle: string
}

/** Problem category : valeur de la base → libellé français, par groupe. */
export const CATEGORIES_FNCT: readonly { groupe: string; categories: readonly CategorieFnct[] }[] = [
  {
    groupe: 'Documentation de conception',
    categories: [
      { valeur: 'Engineering documentation - Process', libelle: 'Process (P&ID, PPL)' },
      { valeur: 'Engineering documentation - Electical', libelle: 'Électrique (EDE)' },
      { valeur: 'Engineering documentation - Mechanical', libelle: 'Mécanique (plans, certificats)' },
      { valeur: 'Engineering documentation - Automation', libelle: 'Automatisme (HDS)' },
    ],
  },
  {
    groupe: 'Fonctionnement',
    categories: [
      { valeur: 'Malfunction - Software', libelle: 'Dysfonctionnement logiciel' },
      { valeur: 'Out Of Specification - Component', libelle: 'Composant hors spécification' },
      { valeur: 'Out Of Specification - System function', libelle: 'Fonction système hors spécification' },
    ],
  },
  {
    groupe: 'Autres',
    categories: [
      { valeur: 'Missing Component / Documentation', libelle: 'Composant ou document manquant' },
      { valeur: 'Engineering Change Control issue', libelle: 'Gestion des modifications (ECC)' },
    ],
  },
]

export const CATEGORIE_PROCESS = 'Engineering documentation - Process'

export function libelleCategorie(valeur: string): string {
  for (const g of CATEGORIES_FNCT) {
    const c = g.categories.find((x) => x.valeur === valeur)
    if (c) return `${g.groupe === 'Documentation de conception' ? 'Documentation — ' : ''}${c.libelle}`
  }
  return valeur
}

/** Place of detection : valeur de la base → libellé. */
export const LIEUX_FNCT: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'I-Test', libelle: 'Test (interne)' },
  { valeur: 'I-FAT', libelle: 'FAT (interne)' },
  { valeur: 'C-SAT', libelle: 'SAT (chez le client)' },
  { valeur: 'C-IQOQ', libelle: 'IQ / OQ (chez le client)' },
]

/**
 * Expert proposé selon la catégorie (Guillaume, 07/10), d'après la base
 * 2023-2026 : Process → Geny 106 / 145 (Morlot 14) ; Électrique → Strauss
 * 4 / 8. Une catégorie absente : pas de proposition (expert trop dispersé :
 * mécanique, composant manquant, hors spécification).
 */
export const EXPERT_PAR_CATEGORIE: Readonly<Record<string, string>> = {
  'Engineering documentation - Process': 'Jean-Philippe Geny',
  'Engineering documentation - Electical': 'Dorian Strauss',
  // Automatisme (Parenty 15, Bourdon 9) et logiciel (Parenty 11, Jeanbille 11) :
  // Olivier Parenty (Guillaume, 07/10, « on affinera à l'usage »).
  'Engineering documentation - Automation': 'Olivier Parenty',
  'Malfunction - Software': 'Olivier Parenty',
}

export const PRIORITES_FNCT: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'Low', libelle: 'Basse' },
  { valeur: 'Medium', libelle: 'Moyenne' },
  { valeur: 'High', libelle: 'Haute' },
]

/** Tests de la procédure qui valident le P&ID et la PPL (Guillaume, 06/10). */
export const TESTS_PID_PPL = {
  pid: '75250',
  pplProcess: '76326',
  pplDoc: '75255',
} as const

export interface Responsable {
  nom: string
  service: string
}

/** Responsables figés (06/10). */
export const RESPONSABLES_FNCT = {
  /** Mise à jour du P&ID et de la PPL : l'un ou l'autre. */
  conceptionProcess: [
    { nom: 'Jean-Philippe Geny', service: 'Product Design' },
    { nom: 'Abel Morlot', service: 'Product Design' },
  ],
  documentation: { nom: 'Adeline Leidner', service: 'Manufacturing' },
  hds: { nom: 'Olivier Parenty', service: 'Product Design' },
  ede: { nom: 'Dorian Strauss', service: 'Product Design' },
  plansMecaniques: { nom: 'Mohamed Maarouf', service: 'Product Design' },
  logiciel: { nom: 'Olivier Parenty', service: 'Product Design' },
} as const satisfies Record<string, Responsable | readonly Responsable[]>

/**
 * Échéances par défaut, en JOURS OUVRÉS (Guillaume, 14/10 : « mise à jour de
 * documentation pour le BE : 10 j ouvrés ») — week-ends et jours fériés sautés
 * (utils/fnct, ajouterJoursOuvres). La documentation suit la conception (« en
 * même temps »), le retest une semaine après.
 */
export const DELAIS_FNCT = {
  conception: 10,
  documentation: 10,
  retest: 15,
  autre: 15,
} as const

// ─── Qui fait quoi (lot F3, Guillaume du 06/10) ───
//
// La revue périodique et la clôture des FNC-T : le coordinateur tests process
// (Sébastien Vautelin) et le coordinateur AQ Opérations (Germain Etienne).
// L'analyse (risque) et la clôture ne se montrent qu'aux coordinateurs, à
// l'AQ / CQ et à l'administrateur. Pendant la construction, tout le chantier
// reste réservé à l'administrateur (gardes des pages).

/** Une personne d'un rôle FNC-T : « Prénom Nom » et son UPN en minuscules. */
export interface PersonneFnctRole {
  nom: string
  upn: string
}

// Valeurs PAR DÉFAUT : le panneau d'administration (22/10) les remplace dès
// qu'on y enregistre les rôles FNC-T (utils/droitsFnct les lit).

/** Coordinateur AQ Opérations : AQ / CQ, et mène la revue avec le coordinateur tests process. */
const AQ_OPERATIONS: PersonneFnctRole = { nom: 'Germain Etienne', upn: 'germain.etienne@sartorius.com' } // 07/10

/**
 * Équipe AQ / CQ (Guillaume, 07/10) : voient l'analyse (risque) et la clôture,
 * gèrent les actions. Clore une FNC-T reste à la revue (REVUE_FNCT_DEFAUT).
 * UPN en minuscules.
 */
export const AQ_CQ_DEFAUT: readonly PersonneFnctRole[] = [
  AQ_OPERATIONS,
  { nom: 'Mickael Mainguy', upn: 'mickael.mainguy@sartorius.com' },
  { nom: 'Cezar Machado', upn: 'cezar.machado@sartorius.com' },
  { nom: 'Kristina Sanchez', upn: 'kristina.sanchez1@sartorius.com' },
  { nom: 'Aurélie Courrier', upn: 'aurelie.courrier@sartorius.com' },
  { nom: 'Hugo Walch', upn: 'hugo.walch@sartorius.com' },
  { nom: 'Corentin Meyer', upn: 'corentin.meyer@sartorius.com' },
  { nom: 'Enzo Engles', upn: 'enzo.engles@sartorius.com' },
]

/**
 * Testeurs du chantier FNC-T (Guillaume, 21/10) : pendant la construction, ils
 * voient et essaient TOUT ce que voit l'administrateur sur les FNC-T (pages
 * /fnct, ouverture, plan d'actions, analyse, clôture) — et rien de plus ailleurs.
 * Leurs écritures partent sous leur propre compte. UPN en minuscules.
 */
export const TESTEURS_FNCT_DEFAUT: readonly PersonneFnctRole[] = [
  { nom: 'Emma Raoult', upn: 'emma.raoult@sartorius.com' },
]

/** Revue et clôture des FNC-T (en plus de l'administrateur). UPN en minuscules. */
export const REVUE_FNCT_DEFAUT: readonly PersonneFnctRole[] = [
  { nom: 'Sébastien Vautelin', upn: 'sebastien.vautelin@sartorius.com' }, // coordinateur tests process
  AQ_OPERATIONS,
]

// ─── Rapport PDF (lot F4) ───

/**
 * Dossier « FNC (Test) » de la qualité (QM_SCE, Guillaume le 07/10) : chaque
 * rapport y est déposé dans un sous-dossier au N° de la FNC-T, comme le
 * faisait la macro (années passées : dans leur dossier d'année). Adresse
 * directe ou lien de partage, les deux sont reconnus (rapportFnct.api).
 * null : le rapport se génère, se télécharge et s'imprime, sans dépôt.
 */
export const LIEN_DOSSIER_RAPPORTS_FNCT: string | null =
  'https://sartorius.sharepoint.com/sites/QM_SCE/Autres%20Documents/Assurance%20Qualit%C3%A9%20(AQ)/Non-Conformit%C3%A9s/FNC%20(Test)'

// ─── Catégories d'action ───

/** Ce que veut dire chaque catégorie d'action, en une ligne (plan d'actions, ouverture). */
export const AIDES_CATEGORIE_ACTION: Record<CategorieAction, string> = {
  CO: 'Contenir et corriger l’écart constaté.',
  RCA: 'Chercher la cause.',
  CA: 'Supprimer la cause, pour que l’écart ne se reproduise pas ici.',
  PA: 'Éviter qu’il se produise ailleurs.',
  OA: 'Une amélioration repérée en passant.',
}
